import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { X, Image, FileText, Table, File, Trash2, Eye, Upload, Folder } from 'lucide-react';
import { mediaAPI } from '../lib/api';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';

const FILE_ICONS = {
  pdf: { icon: FileText, color: 'text-red-500', bg: 'bg-red-50' },
  xlsx: { icon: Table, color: 'text-green-500', bg: 'bg-green-50' },
  xls: { icon: Table, color: 'text-green-500', bg: 'bg-green-50' },
  doc: { icon: FileText, color: 'text-blue-500', bg: 'bg-blue-50' },
  docx: { icon: FileText, color: 'text-blue-500', bg: 'bg-blue-50' },
  png: { icon: Image, color: 'text-purple-500', bg: 'bg-purple-50' },
  jpg: { icon: Image, color: 'text-purple-500', bg: 'bg-purple-50' },
  jpeg: { icon: Image, color: 'text-purple-500', bg: 'bg-purple-50' },
  gif: { icon: Image, color: 'text-purple-500', bg: 'bg-purple-50' },
  webp: { icon: Image, color: 'text-purple-500', bg: 'bg-purple-50' },
  svg: { icon: Image, color: 'text-purple-500', bg: 'bg-purple-50' },
};

const getFileIcon = (filename) => {
  const ext = filename?.split('.').pop()?.toLowerCase() || '';
  return FILE_ICONS[ext] || { icon: File, color: 'text-gray-500', bg: 'bg-gray-50' };
};

const MediaManager = ({ 
  files = [], 
  onFilesChange, 
  maxFiles = 10,
  acceptedFileTypes = ['image/*', '.pdf', '.xlsx', '.xls', '.doc', '.docx'],
  maxFileSize = 5242880,
  showPreview = true,
  onUpload = null,
  uploading = false,
  onUploadingChange = null
}) => {
  const [previewFile, setPreviewFile] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [deletingMediaId, setDeletingMediaId] = useState(null);
  const fileInputRef = useRef(null);

  // Загрузка медиафайлов при каждом открытии галереи (актуальный список после upload)
  useEffect(() => {
    if (!galleryOpen) return undefined;

    let active = true;
    setLoadingMedia(true);
    mediaAPI.getAll().then((files) => {
      if (active) {
        setMediaFiles(files || []);
        setLoadingMedia(false);
      }
    }).catch(() => {
      if (active) {
        setMediaFiles([]);
        setLoadingMedia(false);
      }
    });

    return () => { active = false; };
  }, [galleryOpen]);

  const handleFileSelect = async (event) => {
    const selectedFiles = Array.from(event.target.files);
    const remainingSlots = maxFiles - files.length;
    const filesToAdd = selectedFiles.slice(0, remainingSlots);

    if (onUpload) {
      onUploadingChange?.(true);
      try {
        const uploadedFiles = [];
        for (const file of filesToAdd) {
          const result = await onUpload(file);
          uploadedFiles.push({
            id: result.id || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            name: result.name || file.name,
            type: result.type || file.type,
            size: result.size || file.size,
            url: result.url,
            isNew: false,
            uploaded: true,
          });
        }
        onFilesChange([...files, ...uploadedFiles]);
      } catch (error) {
        console.error('Upload error:', error);
      } finally {
        onUploadingChange?.(false);
      }
    } else {
      const newFiles = filesToAdd.map(file => ({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: file.name,
        type: file.type,
        size: file.size,
        url: URL.createObjectURL(file),
        isNew: true,
        file: file,
      }));
      onFilesChange([...files, ...newFiles]);
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const filteredFiles = useMemo(() => {
    if (!Array.isArray(files)) return [];
    if (!searchTerm) return files;
    return files.filter(f => 
      f.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.url?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [files, searchTerm]);

  const handleRemoveFile = (index) => {
    const fileToRemove = filteredFiles[index];
    if (fileToRemove) {
      const realIndex = files.findIndex(f => f.id === fileToRemove.id);
      if (realIndex !== -1) {
        const newFiles = [...files];
        newFiles.splice(realIndex, 1);
        onFilesChange(newFiles);
      }
    }
  };

  const handleGalleryDelete = async (file, event) => {
    event.stopPropagation();
    if (!file?.url) return;
    if (!window.confirm(`Удалить «${file.name}» из медиабиблиотеки?`)) return;

    setDeletingMediaId(file.id);
    try {
      await mediaAPI.remove(file.url);
      setMediaFiles((prev) => prev.filter((item) => item.id !== file.id));
      if (files.some((item) => item.url === file.url)) {
        onFilesChange(files.filter((item) => item.url !== file.url));
      }
    } catch (error) {
      console.error('Delete media error:', error);
      window.alert(error.message || 'Не удалось удалить файл');
    } finally {
      setDeletingMediaId(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getAcceptedTypesString = () => {
    return acceptedFileTypes.join(',');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          {files.length} / {maxFiles} файлов
        </span>
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={getAcceptedTypesString()}
            onChange={handleFileSelect}
            className="hidden"
            id="media-file-upload"
          />
          <Button 
            type="button" 
            variant="outline" 
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={files.length >= maxFiles || uploading}
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? 'Загрузка...' : 'Загрузить'}
          </Button>
          <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
            <DialogTrigger asChild>
              <Button type="button" variant="outline" size="sm">
                <Folder className="w-4 h-4 mr-2" />
                Выбрать
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Медиабиблиотека</DialogTitle>
                <DialogDescription>
                  Выберите изображение для подстановки в поле. Крестик удаляет файл из библиотеки.
                </DialogDescription>
              </DialogHeader>
              {loadingMedia ? (
                <div className="text-center py-8">Загрузка...</div>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {mediaFiles.filter(f => f.type?.startsWith('image/')).map((file) => (
                    <div
                      key={file.id}
                      className="group relative aspect-square border rounded-lg overflow-hidden cursor-pointer hover:border-accent hover:ring-2 hover:ring-accent/50 transition-all"
                      onClick={() => {
                        const newItem = {
                          id: file.id,
                          url: file.url,
                          name: file.name,
                          type: file.type,
                          size: file.size,
                        };
                        if (maxFiles === 1) {
                          onFilesChange([newItem]);
                        } else {
                          onFilesChange([...files, newItem]);
                        }
                        setGalleryOpen(false);
                      }}
                    >
                      <img src={file.url} alt={file.name} className="w-full h-full object-cover" />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-1 right-1 h-6 w-6 z-10 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                        disabled={deletingMediaId === file.id}
                        aria-label={`Удалить ${file.name}`}
                        onClick={(event) => handleGalleryDelete(file, event)}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              {mediaFiles.filter(f => f.type?.startsWith('image/')).length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  Нет загруженных изображений
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {files.length > 0 && (
        <>
          <Input
            placeholder="Поиск файлов..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="text-sm"
          />
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {Array.isArray(filteredFiles) && filteredFiles.map((file, index) => {
              const { icon: Icon, color, bg } = getFileIcon(file.name);
              const isImage = file.type?.startsWith('image/') || /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(file.name);
              
              return (
                <div 
                  key={file.id || index} 
                  className="relative group border rounded-lg overflow-hidden bg-card"
                >
                  {isImage && showPreview ? (
                    <div className="aspect-square bg-muted flex items-center justify-center overflow-hidden">
                      <img 
                        src={file.url} 
                        alt={file.name}
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className={`aspect-square ${bg} flex items-center justify-center`}>
                      <Icon className="w-12 h-12" style={{ color }} />
                    </div>
                  )}
                  
                  <div className="p-2">
                    <p className="text-xs font-medium truncate" title={file.name}>
                      {file.name}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {formatFileSize(file.size)}
                    </p>
                  </div>

                  <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {isImage && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => setPreviewFile(file)}
                      >
                        <Eye className="w-3 h-3" />
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => handleRemoveFile(index)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>

                  {file.isNew && (
                    <Badge variant="secondary" className="absolute top-1 left-1 text-[10px] h-5">
                      new
                    </Badge>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {files.length === 0 && (
        <div 
          className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
          onClick={() => fileInputRef.current?.click()}
        >
          <Image className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">
            Нажмите для загрузки файлов
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Изображения, PDF, Excel, Word
          </p>
        </div>
      )}

      {previewFile && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPreviewFile(null)}
        >
          <div className="relative max-w-full max-h-full">
            <img 
              src={previewFile.url} 
              alt={previewFile.name}
              className="max-w-full max-h-[90vh] object-contain"
            />
            <Button
              type="button"
              variant="secondary"
              size="icon"
              className="absolute top-2 right-2"
              onClick={() => setPreviewFile(null)}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaManager;
